import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowRight, Loader2 } from 'lucide-react'
import { ApiError, inquiriesApi } from '@/utils/api'

type ContactFormState = {
    email: string
    fullName: string
    message: string
}

const EMPTY_STATE: ContactFormState = {
    email: '',
    fullName: '',
    message: '',
}

const MAX_MESSAGE_LENGTH = 500

export default function ContactForm() {
    const [form, setForm] = useState<ContactFormState>(EMPTY_STATE)
    const [submitting, setSubmitting] = useState(false)
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

    const handleChange =
        (field: keyof ContactFormState) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = event.target.value
            if (field === 'message' && value.length > MAX_MESSAGE_LENGTH) return
            setForm((prev) => ({ ...prev, [field]: value }))
        }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (submitting) return
        setSubmitting(true)
        setStatus(null)

        try {
            await inquiriesApi.contact({
                fullName: form.fullName,
                email: form.email,
                message: form.message,
            })
            setStatus({ type: 'success', message: 'Thanks! Your message has been sent — we&apos;ll get back to you soon.' })
            setForm(EMPTY_STATE)
        } catch (error) {
            const message =
                error instanceof ApiError
                    ? error.message
                    : 'Something went wrong sending your message. Please try again.'
            setStatus({ type: 'error', message })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
                <label htmlFor="contact-email" className="block text-sm lg:text-base font-medium text-gray-700 text-start">
                    Email
                </label>
                <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 lg:py-4 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="contact-name" className="block text-sm lg:text-base font-medium text-gray-700 text-start">
                    Full Name
                </label>
                <input
                    id="contact-name"
                    name="fullName"
                    type="text"
                    required
                    value={form.fullName}
                    onChange={handleChange('fullName')}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-3 lg:py-4 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="contact-message" className="block text-sm lg:text-base font-medium text-gray-700 text-start">
                    Message
                </label>
                <textarea
                    id="contact-message"
                    name="message"
                    rows={6}
                    required
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Enter message here"
                    className="w-full px-4 py-3 lg:py-4 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#f60]"
                />
                <div className="text-right text-xs lg:text-sm text-gray-400">
                    {form.message.length}/{MAX_MESSAGE_LENGTH}
                </div>
            </div>

            {status && (
                <p
                    className={`text-sm text-center ${
                        status.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}
                    role="status"
                >
                    {status.message}
                </p>
            )}

            <div className="flex justify-center pt-4">
                <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-8 sm:px-12 lg:px-16 py-4 sm:py-5 lg:py-6 text-base lg:text-lg font-semibold transition-all group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                            Sending...
                        </>
                    ) : (
                        <>
                            Submit
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}
